import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Button,
	// Dropdown,
	// DropdownMenuButton,
	// IconButton,
	Loading,
} from "@alm/flow-ds/components";
import Element from "../Element";
import useForm from "hooks/useForm";
import PageHeader from "components/PageHeader";
import { useTranslation } from "react-i18next";
import TaskPageTemplate from "components/Templates/TaskPageTemplate";
import { WorkbenchContext } from "contexts/WorkbenchContext";
import UnsavedChanges from "../UnsavedChanges";
import useLinkedForm from "hooks/useLinkedForm";
import { WorkbenchLayer } from "components/Workbench";
import ReactFocusLock from "react-focus-lock";
import ErrorBoundary from "components/Errors/ErrorBoundary";
import TaskError from "components/ActivityForm/TaskError";
import Breadcrumb from "components/Breadcrumb";
import { useHistory } from "react-router-dom";
import { State } from "data";
import { useSelector } from "react-redux";
import { useMaxFormWidth } from "../TaskForm";

interface Props {
	fid: string;
	workbenchIndex: number;
	breadcrumb: { name: string; link?: string }[];
}

function LinkedForm({ fid, workbenchIndex, breadcrumb }: Props) {
	const { t } = useTranslation();
	const history = useHistory();

	const formLayout = useSelector(
		(state: State) => state?.forms[fid]?.server?.formLayout
	);

	// Get current form
	const form = useForm(fid);

	// Get linked form
	const linked = useLinkedForm(fid, workbenchIndex);

	const [locationKeys, setLocationKeys] = useState<string[]>([]);

	const [closePrompt, setClosePrompt] = useState(false);

	// Scroll actice or not
	const [isScrollActive, setIsScrollActive] = useState(false);

	// Scrolling element
	const formWindow = document.querySelector(`.workbench-layer:last-child`);

	// Build the breadcrumb for the linked form
	const crumb = useMemo(
		() => [
			...breadcrumb,
			{
				name: form.label,
			},
		],
		[breadcrumb, form.label]
	);

	// Remove the first item in the array since its the 'Tasks' crumb
	const formCrumb = useMemo(() => crumb.splice(1, crumb.length), [crumb]);

	/**
	 * Load the child form if there is a linked form
	 */
	useEffect(() => {
		if (form.ready && !linked.init && linked.hasChild) {
			linked.loadChild();
		}
	}, [form.ready, linked, linked.hasChild]);

	useEffect(() => {
		return history.listen(location => {
			if (history.action === "PUSH") {
				setLocationKeys([location.key || ""]);
			}

			if (history.action === "POP" && locationKeys[1] !== location.key) {
				setLocationKeys(keys => [location.key || "", ...keys]);
				safeClose();
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locationKeys]);

	// Index of the current form
	const wbContext = useMemo(() => {
		return {
			workbenchIndex,
		};
	}, [workbenchIndex]);

	const close = useCallback(() => {
		form.close();
	}, [form]);

	const safeClose = useCallback(() => {
		if (form.dirty && !form.error) {
			setClosePrompt(true);
		} else {
			close();
		}
	}, [close, form.dirty, form.error]);

	useEffect(() => {
		// If scroll is bigger than the breadcrumb we set scroll is active
		formWindow?.addEventListener("scroll", () => {
			if (formWindow.scrollTop > 60) {
				setIsScrollActive(true);
			} else {
				setIsScrollActive(false);
			}
		});
	}, [formWindow]);

	const { maxWidth, maxFormWidth } = useMaxFormWidth(formLayout);

	return (
		<ErrorBoundary>
			<ReactFocusLock
				className="workbench-layer__editor"
				returnFocus={true}>
				<Breadcrumb
					crumb={formCrumb}
					className="container"
					style={{ maxWidth }}
				/>
				<PageHeader
					title={form.label}
					sticky
					style={{ maxWidth }}
					className="container page-header__title"
					scroll={isScrollActive}>
					<div className="page-header__actions">
						{form.patching && (
							<Loading className="elem__loader--md me-2" />
						)}

						{/* Small screen action dropdown */}
						{/* <Dropdown
							className="d-md-none"
							id="activityForm__dropdown"
							menuBtn={
								<IconButton
									className="btn-primary"
									icon="ellipsis-v"
									label={t(
										"main.icons.menuOptions"
									)}></IconButton>
							}
							align="right">
							<DropdownMenuButton
								disabled={!form.canSave}
								onClick={form.save}
								label={t("common.labels.save")}
							/>
							<DropdownMenuButton
								disabled={!form.canClose}
								onClick={safeClose}
								label={t("common.labels.close")}
							/>
						</Dropdown> */}

						{/* Large screen action buttons */}
						{/* <div className="page-header__actions d-none d-md-block"> */}
						<div className="page-header__actions">
							<div className="page-header__actions-buttons">
								<Button
									className="btn-outline-primary"
									disabled={!form.canClose}
									onClick={safeClose}
									label={t("common.labels.close")}
								/>
								<Button
									className="btn-primary"
									disabled={!form.canSave}
									onClick={form.save}
									label={t("common.labels.save")}
								/>
							</div>
						</div>
					</div>
				</PageHeader>
				{/* form bits */}
				<div
					className="container"
					style={{
						maxWidth,
					}}>
					<div className="page-content">
						<WorkbenchContext.Provider value={wbContext}>
							<TaskPageTemplate
								style={{
									maxWidth: maxFormWidth,
								}}>
								<header>
									<h2 className="form__label">
										{form.label}
									</h2>
								</header>

								{form.error ? (
									<TaskError error={form.error} />
								) : !form.ready ? (
									<Loading className="elem__loader--lg mt-5" />
								) : (
									<Element
										rowIndex={0}
										fid={fid}
										uuid={form.uuid}
									/>
								)}
							</TaskPageTemplate>
						</WorkbenchContext.Provider>
					</div>
				</div>
				{/* Visible on small devices only */}
				{/* {form.ready && (
					<div className="page-footer workbench-footer container visible d-md-none">
						<Button
							className="btn-outline-primary workbench-footer__action-btn "
							disabled={!form.canClose}
							onClick={safeClose}
							label={t("common.labels.close")}
						/>
						<Button
							className="btn-primary workbench-footer__action-btn "
							disabled={!form.canSave}
							onClick={form.save}
							label={t("common.labels.save")}
						/>
					</div>
				)} */}

				{closePrompt && form.ready && (
					<UnsavedChanges
						closeBtnClick={form.close}
						saveAndCloseBtnClick={form.saveAndClose}
						cancelBtnClick={() => setClosePrompt(false)}
					/>
				)}
			</ReactFocusLock>

			{linked.init && (
				<WorkbenchLayer>
					{linked.child ? (
						<LinkedForm
							fid={linked.child}
							workbenchIndex={workbenchIndex + 1}
							breadcrumb={crumb}
						/>
					) : (
						<div className="workbench-layer__editor">
							<Loading className="elem__loader--lg mt-5" />
						</div>
					)}
				</WorkbenchLayer>
			)}
		</ErrorBoundary>
	);
}

export default React.memo(LinkedForm);
